import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  SwipeableDrawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Tooltip,
  Stack,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import { useAuth } from '../../hooks/useAuth';
import { useColorMode } from '../../context/ColorModeContext';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import { activeNavItem } from './navConfig';

const COLLAPSE_KEY = 'skyline-sidebar-collapsed';

const DashboardLayout = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const { mode, toggleColorMode } = useColorMode();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const [anchorEl, setAnchorEl] = useState(null);

  const drawerWidth = collapsed ? theme.custom.drawerWidthCollapsed : theme.custom.drawerWidth;
  const appBarHeight = theme.custom.appBarHeight;

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, prev ? '0' : '1');
      } catch (e) {
        // best effort
      }
      return !prev;
    });
  };

  // Close the mobile drawer whenever the route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const pageTitle = activeNavItem(location.pathname)?.text || 'Skyline';
  const canSeeSettings = ['admin', 'manager'].includes(user?.role);

  const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      {/* Top bar — glass over the content area */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: alpha(theme.palette.background.default, 0.72),
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          color: 'text.primary',
          borderBottom: `1px solid ${theme.custom.cardBorder}`,
          transition: theme.transitions.create(['width', 'margin-left'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: `${appBarHeight}px !important`, px: { xs: 2, md: 3 }, gap: 1 }}>
          <IconButton
            aria-label={isMobile ? 'Open navigation menu' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={isMobile ? () => setMobileOpen(true) : toggleCollapsed}
            edge="start"
            sx={{ color: 'text.secondary' }}
          >
            {isMobile ? <MenuRoundedIcon /> : collapsed ? <MenuRoundedIcon /> : <MenuOpenRoundedIcon />}
          </IconButton>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              noWrap
              sx={{ fontFamily: '"Sora", sans-serif', fontWeight: 700, fontSize: '1.05rem' }}
            >
              {pageTitle}
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
              <IconButton
                onClick={toggleColorMode}
                aria-label="Toggle color mode"
                sx={{ color: 'text.secondary' }}
              >
                {mode === 'light' ? (
                  <DarkModeRoundedIcon fontSize="small" />
                ) : (
                  <LightModeRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title={user?.name || 'Account'}>
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-label="Open account menu"
                aria-haspopup="true"
                sx={{ p: 0.5 }}
              >
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    fontSize: '0.9rem',
                    backgroundImage: theme.custom.gradients.brand,
                  }}
                >
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Stack>

          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            slotProps={{ paper: { sx: { minWidth: 220 } } }}
          >
            <Box sx={{ px: 2, py: 1.25 }}>
              <Typography variant="subtitle2" noWrap>
                {user?.name || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {user?.email || ''}
              </Typography>
            </Box>
            <Divider sx={{ mb: 0.5 }} />
            {canSeeSettings && (
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  navigate('/settings');
                }}
              >
                <ListItemIcon>
                  <SettingsRoundedIcon fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
            )}
            <MenuItem onClick={logout} sx={{ color: 'error.main' }}>
              <ListItemIcon sx={{ color: 'error.main' }}>
                <LogoutRoundedIcon fontSize="small" />
              </ListItemIcon>
              Log out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Desktop sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.shorter,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        <Sidebar collapsed={collapsed} />
      </Drawer>

      {/* Mobile drawer */}
      <SwipeableDrawer
        variant="temporary"
        open={mobileOpen}
        onOpen={() => setMobileOpen(true)}
        onClose={() => setMobileOpen(false)}
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 'min(84vw, 320px)',
            boxSizing: 'border-box',
            borderTopRightRadius: 20,
            borderBottomRightRadius: 20,
          },
        }}
      >
        <Sidebar onNavigate={() => setMobileOpen(false)} />
      </SwipeableDrawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: '100dvh',
          pt: `${appBarHeight}px`,
          pb: { xs: `calc(${theme.custom.bottomNavHeight}px + env(safe-area-inset-bottom) + 16px)`, md: 4 },
          backgroundColor: 'background.default',
        }}
      >
        <Box
          key={location.pathname}
          className="page-transition"
          sx={{
            maxWidth: 1440,
            mx: 'auto',
            px: { xs: 2, sm: 2.5, md: 3.5 },
            py: { xs: 2.5, md: 3.5 },
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Mobile bottom dock */}
      <BottomNav onOpenMenu={() => setMobileOpen(true)} />
    </Box>
  );
};

export default DashboardLayout;
